import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { recordPageView, fetchPageViewStats, getUserData } from '../../api/api';
import styles from './LeaguePromo.module.css';
// Desktop screenshots
import mainViewImg from '../../assets/images/league/main_view.png';
import groupsImg from '../../assets/images/league/groups.png';
import matchPlanningImg from '../../assets/images/league/match_planing.png';
import rankingImg from '../../assets/images/league/ranking.png';
import rankingDetailsImg from '../../assets/images/league/ranking_details.png';
import awardsImg from '../../assets/images/league/awards.png';
// Mobile screenshots
import mainViewMobileImg from '../../assets/images/league/main_view_mobile.png';
import groupsMobileImg from '../../assets/images/league/groups_mobile.png';
import matchPlanningMobileImg from '../../assets/images/league/match_planing_mobile.png';
import rankingMobileImg from '../../assets/images/league/ranking_mobile.png';
import awards1MobileImg from '../../assets/images/league/awards1_mobile.png';
import awards2MobileImg from '../../assets/images/league/awards2_mobile.png';

const PAGE_PATH = '/liga-grupowa';
const SESSION_KEY = 'liga-grupowa-viewed';

const LeaguePromo = () => {
    const [stats, setStats] = useState(null);
    const [isSuper, setIsSuper] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const user = await getUserData();
            const authorities = new Set(user?.authorities || []);
            const userIsSuper = authorities.has('ROLE_SUPER');

            // Increment view counter once per browser session — but never for SUPER
            if (!userIsSuper && !sessionStorage.getItem(SESSION_KEY)) {
                await recordPageView(PAGE_PATH);
                sessionStorage.setItem(SESSION_KEY, '1');
            }

            if (cancelled) return;
            if (userIsSuper) {
                setIsSuper(true);
                const result = await fetchPageViewStats(PAGE_PATH);
                if (!cancelled && result.success) setStats(result.data);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div className={styles.page}>
            {isSuper && stats && (
                <div className={styles.viewCounter}>
                    👁 Wyświetleń — dziś: <strong>{stats.today}</strong>
                    {' · '}
                    ten tydzień: <strong>{stats.thisWeek}</strong>
                    {' · '}
                    razem: <strong>{stats.total}</strong>
                </div>
            )}

            {/* HERO */}
            <section className={styles.hero}>
                <h1 className={styles.heroTitle}>Liga Grupowa BTT</h1>
                <p className={styles.heroTagline}>Wejdź do gry</p>
                <p className={styles.heroSubtitle}>Rywalizacja na Twoim poziomie. Dobra zabawa gwarantowana.</p>
                <p className={styles.heroSeason}>Sezon 2026: Maj – Sierpień</p>
                <Link to="/tournaments" className={styles.ctaButton}>Zapisz się</Link>
            </section>

            {/* DLACZEGO LIGA? */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Dlaczego Liga?</h2>
                <div className={styles.cardsGrid}>
                    <div className={styles.card}>
                        <div className={styles.cardIcon}>🎯</div>
                        <h3 className={styles.cardTitle}>Rywale na Twoim poziomie</h3>
                        <p className={styles.cardText}>
                            Grupy A–E dopasowane do poziomu gry. Grasz z rywalami
                            o podobnych umiejętnościach — emocjonujące mecze zamiast
                            jednostronnych wyników.
                        </p>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardIcon}>📅</div>
                        <h3 className={styles.cardTitle}>Graj kiedy Ci pasuje</h3>
                        <p className={styles.cardText}>
                            Sam proponujesz terminy, rywal akceptuje jednym kliknięciem.
                            Kort rezerwuje się automatycznie. 18 dni na rozegranie meczów w rundzie.
                        </p>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardIcon}>📈</div>
                        <h3 className={styles.cardTitle}>Rozwijaj się</h3>
                        <p className={styles.cardText}>
                            Wygrywasz? Awansujesz do wyższej grupy.
                            Każda runda to nowe wyzwanie i nowi rywale.
                        </p>
                    </div>
                </div>
                <div className={styles.screenshotWrapper}>
                    <div className={styles.phoneMockup}>
                        <picture>
                            <source media="(max-width: 640px)" srcSet={mainViewMobileImg} />
                            <img src={mainViewImg} alt="Widok główny ligi" className={styles.screenshot} />
                        </picture>
                    </div>
                </div>
            </section>

            {/* JAK TO DZIAŁA — TIMELINE */}
            <section className={`${styles.section} ${styles.sectionAlt}`}>
                <h2 className={styles.sectionTitle}>Jak to działa?</h2>
                <div className={styles.timeline}>
                    <div className={styles.timelineItem}>
                        <div className={styles.timelineDot}>1</div>
                        <p className={styles.timelineLabel}>Maj — Start ligi</p>
                        <p className={styles.timelineText}>
                            Podział na grupy A–E według poziomu gry.
                            Każda grupa to 4–5 graczy o podobnych umiejętnościach.
                        </p>
                    </div>
                    <div className={styles.timelineItem}>
                        <div className={styles.timelineDot}>2</div>
                        <p className={styles.timelineLabel}>Runda (18 dni) — 6 rund w sezonie</p>
                        <p className={styles.timelineText}>
                            Rozgrywasz mecze z rywalami w swojej grupie.
                            Proponujesz terminy w systemie, rywal akceptuje — kort rezerwuje się automatycznie.
                        </p>
                    </div>
                    <div className={styles.timelineItem}>
                        <div className={styles.timelineDot}>3</div>
                        <p className={styles.timelineLabel}>Koniec rundy</p>
                        <p className={styles.timelineText}>
                            Najlepsi awansują do wyższej grupy, najsłabsi spadają.
                            Nowa runda — nowi rywale, nowe emocje.
                        </p>
                    </div>
                    <div className={styles.timelineItem}>
                        <div className={styles.timelineDot}>4</div>
                        <p className={styles.timelineLabel}>Wrzesień — Playoffy</p>
                        <p className={styles.timelineText}>
                            Top 8 z rankingu ogólnego gra w playoffach w systemie brazylijskim
                            o tytuł mistrza ligi. Wręczenie wyróżnień w 9 kategoriach.
                        </p>
                    </div>
                </div>
                <div className={styles.screenshotWrapper}>
                    <div className={styles.phoneMockup}>
                        <picture>
                            <source media="(max-width: 640px)" srcSet={groupsMobileImg} />
                            <img src={groupsImg} alt="Grupy ligowe" className={styles.screenshot} />
                        </picture>
                    </div>
                </div>
            </section>

            {/* CO ZYSKUJESZ? */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Co zyskujesz?</h2>
                <div className={styles.benefitsGrid}>
                    <div className={styles.benefitCard}>
                        <span className={styles.benefitIcon}>🎾</span>
                        <div>
                            <h4 className={styles.benefitTitle}>Emocjonujące mecze</h4>
                            <p className={styles.benefitText}>
                                System grup sprawia, że każdy mecz jest wyrównany.
                                Bonus punktowy za samo rozegranie meczu — liczy się aktywność.
                            </p>
                        </div>
                    </div>
                    <div className={styles.benefitCard}>
                        <span className={styles.benefitIcon}>❄️</span>
                        <div>
                            <h4 className={styles.benefitTitle}>Elastyczność</h4>
                            <p className={styles.benefitText}>
                                Potrzebujesz przerwy? Zamróź udział na 1 rundę — max 2 razy w sezonie.
                                Seria zwycięstw nie zostaje przerwana, a w rankingu dostajesz 80% kompensację.
                            </p>
                        </div>
                    </div>
                    <div className={styles.benefitCard}>
                        <span className={styles.benefitIcon}>🏅</span>
                        <div>
                            <h4 className={styles.benefitTitle}>9 wyróżnień</h4>
                            <p className={styles.benefitText}>
                                Najdłuższa seria, król powrotów, wojownik...
                                Nie musisz wygrać ligi, żeby zostać zauważonym.
                            </p>
                        </div>
                    </div>
                    <div className={styles.benefitCard}>
                        <span className={styles.benefitIcon}>⚡</span>
                        <div>
                            <h4 className={styles.benefitTitle}>Pełna automatyzacja</h4>
                            <p className={styles.benefitText}>
                                Planowanie meczów, rezerwacja kortów, ranking, statystyki
                                — wszystko w jednym systemie.
                            </p>
                        </div>
                    </div>
                    <div className={styles.benefitCard}>
                        <span className={styles.benefitIcon}>🎾</span>
                        <div>
                            <h4 className={styles.benefitTitle}>Sprawiedliwy dyżur piłkowy</h4>
                            <p className={styles.benefitText}>
                                System sam wyznacza, kto przynosi piłki na każdy mecz, pilnując
                                równego parytetu procentowego <em>piłki dostarczone / mecze rozegrane</em>{' '}
                                dla wszystkich graczy. Nikt nie dostarcza częściej niż inni.
                            </p>
                        </div>
                    </div>
                </div>
                <p className={styles.rulesLink}>
                    <Link to="/competition-descriptions">Szczegółowe zasady punktacji →</Link>
                </p>
                <div className={styles.screenshotWrapper}>
                    <div className={styles.phoneMockup}>
                        <picture>
                            <source media="(max-width: 640px)" srcSet={matchPlanningMobileImg} />
                            <img src={matchPlanningImg} alt="Planowanie meczów" className={styles.screenshot} />
                        </picture>
                    </div>
                </div>
            </section>

            {/* RANKING I WYRÓŻNIENIA */}
            <section className={`${styles.section} ${styles.sectionAlt}`}>
                <h2 className={styles.sectionTitle}>Ranking i wyróżnienia</h2>
                <p className={styles.sectionIntro}>
                    Ranking ogólny działa na zasadzie <strong>dwustopniowej premii</strong>: za mecze w grupie
                    otrzymujesz punkty, powiększone o <em>premię grupową</em> (im wyższa grupa, tym większa)
                    oraz <em>bonus za miejsce</em> zajęte w grupie. Awans się opłaca — ale wygrywając w niższej
                    grupie też możesz wspinać się wysoko w rankingu.
                </p>
                <div className={styles.awardsBadges}>
                    <span className={styles.badge}>🏆 Najwięcej wygranych</span>
                    <span className={styles.badge}>🔥 Najdłuższa seria</span>
                    <span className={styles.badge}>📊 Bilans gemów</span>
                    <span className={styles.badge}>🎯 Najwyższy % wygranych</span>
                    <span className={styles.badge}>📈 Bilans setów</span>
                    <span className={styles.badge}>⚡ Wygrane tiebreaki</span>
                    <span className={styles.badge}>💪 Najbardziej dominujący</span>
                    <span className={styles.badge}>👑 Król powrotów</span>
                    <span className={styles.badge}>⚔️ Wojownik</span>
                </div>

                {/* Desktop: ranking (widok podstawowy + szczegóły) */}
                <div className={styles.screenshotDual}>
                    <img src={rankingImg} alt="Ranking ogólny" className={styles.screenshotDualImg} />
                    <img src={rankingDetailsImg} alt="Ranking ogólny — szczegóły punktacji" className={styles.screenshotDualImg} />
                </div>
                {/* Desktop: wyróżnienia */}
                <div className={styles.screenshotSingle}>
                    <img src={awardsImg} alt="Wyróżnienia ligowe" className={styles.screenshotSingleImg} />
                </div>

                {/* Mobile: stacked */}
                <div className={styles.screenshotMobileStack}>
                    <div className={styles.phoneMockup}>
                        <img src={rankingMobileImg} alt="Ranking ogólny" className={styles.screenshot} />
                    </div>
                    <div className={styles.phoneMockup}>
                        <img src={awards1MobileImg} alt="Wyróżnienia ligowe (1)" className={styles.screenshot} />
                    </div>
                    <div className={styles.phoneMockup}>
                        <img src={awards2MobileImg} alt="Wyróżnienia ligowe (2)" className={styles.screenshot} />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className={styles.ctaSection}>
                <h2 className={styles.ctaTitle}>Wejdź do gry już teraz!</h2>
                <p className={styles.ctaText}>Zapisy otwarte do końca kwietnia. Ilość miejsc ograniczona.</p>
                <Link to="/tournaments" className={styles.ctaButtonLarge}>Przejdź do zapisów</Link>
            </section>

        </div>
    );
};

export default LeaguePromo;
